import { Injectable } from '@angular/core';
import { AuthConfig, OAuthErrorEvent, OAuthInfoEvent, OAuthService } from 'angular-oauth2-oidc';
import { AppConfigService } from 'src/app/core/app-config/app-config-service';
import { LoggerService } from 'src/app/core/logger/logger.service';
import { UserManagementDataService } from '../data/user-management-data-service';
import { StoreService, StoreKeys } from '../store/ngrx-store.service';
import { Operations } from '../guards/enum/operations';
import { Configuration } from 'src/app/shared/models/configuration.model';
import { environment } from 'src/environments/environment';
import { BehaviorSubject, interval } from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { UserRole } from 'src/app/shared/models/constants/user-role.constant';
@Injectable()
export class AuthService {

  private configuration: Configuration;
  private authConfig: AuthConfig;
  private userDetails: any;
  isUserDataLoaded$ = new BehaviorSubject<boolean>(false);

  constructor(
    private appConfigService: AppConfigService,
    private oauthService: OAuthService,
    private loggerService: LoggerService,
    private storeService: StoreService,
    private userManagementService: UserManagementDataService,
    private httpClient: HttpClient) {
  }

  async login() {
    await this.setConfiguration();

    if (environment.enableAuthentication) {
      this.loggerService.log('Login process is began.');      
      const data: any = await this.storeService.Get(StoreKeys.UserDetails).toPromise();
      if (!data) {
        this.oauthService.tryLogin()
          .then(async () => {
            if (!this.oauthService.hasValidAccessToken()) {
              this.oauthService.initImplicitFlow();
            }
            if (this.oauthService.hasValidAccessToken()) {
              this.getUserDetailsFromService();
            }
          });
      } else {
        this.userDetails = data;
      }
    }
    return true;
  }

  async setConfiguration() {
    this.configuration = await this.appConfigService.setConfiguration();
    this.authConfig = this.appConfigService.setupAuthConfig(this.configuration);

    this.oauthService.configure(this.authConfig);
  }

  getPostLogoutRedirectUrl() {
    return this.oauthService.postLogoutRedirectUri;
  }

  getRedirectUrl(): string {
    return this.oauthService.redirectUri;
  }

  getAccessToken(): string {
    return this.oauthService.getAccessToken();
  }

  getIdToken(): string {
    return this.oauthService.getIdToken();
  }

  getClaims() {
    return this.oauthService.getIdentityClaims();
  }

  isLoggedIn() {
    return this.oauthService.hasValidIdToken() && this.oauthService.hasValidAccessToken();
  }

  logOut() {
    const httpOptions = {
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
        })
      };
    const sessionId = this.getSessionId();
    if (sessionId !== null) {
        const queryApiBaseUrl = this.appConfigService.configuration.apiConfig.queryBaseUrl;
        const url = queryApiBaseUrl + 'authentication/logout/' + sessionId;
        this.httpClient.post(url, null, httpOptions).subscribe(() => {
            this.oauthService.logOut();
            window.location.reload();
        });
    }
}

getSessionId(): string {
    const idClaims = this.oauthService.getIdentityClaims();
    type ObjectKey = keyof typeof idClaims;
    if (idClaims === null || idClaims === undefined) {
        return null;
    }
    return idClaims['pi.sri' as ObjectKey];
}

setupSessionValidator() {
  interval(this.configuration.authConfiguration.sessionValidationInterval * 1000)
  .subscribe(() => {
      const sessionId = this.getSessionId();
      if (sessionId !== null) {
          const queryApiBaseUrl = this.appConfigService.configuration.apiConfig.queryBaseUrl;
          const url = queryApiBaseUrl + 'authentication/session/' + sessionId + '/status';
          this.httpClient.get(url).subscribe((data: any) => {
            if (data !== null && data.isRevoked) {
              this.oauthService.logOut();
              window.location.reload();
            }
          });
      }
  });
}

  getUserDetailsFromService() {
    this.userManagementService.getUserData().subscribe(async userData => {
      const isInternalUser = userData.userType === UserRole.RegionalInternalUser;
      this.userDetails = userData;
      await this.loadLocale(userData.languageIsoCode);
      this.setupSessionValidator();
      this.userManagementService.getUserPermission(isInternalUser).subscribe(userPermission => {
        // If internal user we need to get all the Operations of the logged in user
        if (isInternalUser) {
          this.userManagementService.getUserApplicationPermissions('EOL').subscribe(internalUserPermissions => {
            // Adding Property hasProductManagementPermission to check in authGuard
            // if the user has permission to login to application or not
            userData.hasProductManagementPermission =
              internalUserPermissions.applicationOperationNames.indexOf(Operations[Operations.CanManageAndPlaceOrderforProducts]) > -1;
            this.storeService.Save(StoreKeys.UserDetails, userData, true);
            this.storeService.Save(StoreKeys.UserPermission, userPermission, true);
            this.isUserDataLoaded$.next(true);
          });
        } else {
          const userOperations = JSON.stringify(userPermission.map((x: any) => x.operations));
          // Adding Property hasProductManagementPermission to check in authGuard
          // if the user has permission to login to application or not
          userData.hasProductManagementPermission
            = userOperations.indexOf(Operations[Operations.CanManageAndPlaceOrderforProducts]) > -1;
          this.storeService.Save(StoreKeys.UserDetails, userData, true);
          this.storeService.Save(StoreKeys.UserPermission, userPermission, true);
          this.isUserDataLoaded$.next(true);
        }
      });

    });
    return this.userDetails;
  }

  getUserDetails() {
    return this.userDetails;
  }

  getUserLocale() {
    return this.userDetails.languageIsoCode;
  }

  loadLocale(userLang: string): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const script = document.createElement('script');
      const defaultLanguage = 'en';
      const baseUrl = window.location.href.split('#')[0];
      script.src = baseUrl + `assets/locales/${userLang}.js`;
      if (!this.doesFileExist(script.src)) {
        script.src = baseUrl + `assets/locales/${defaultLanguage}.js`;
        this.userDetails.languageIsoCode = defaultLanguage;
      }
      script.onload = () => { resolve(); };
      script.onerror = () => { reject(); };
      document.body.appendChild(script);
    });
  }

  private doesFileExist(filePath: any) {
    const xhr = new XMLHttpRequest();
    xhr.open('HEAD', filePath, false);
    xhr.send();
    return xhr.status !== 404;
  }
}
